import { CONTENT_STATUS_LABELS, OVERDUE_LABEL, type ContentStatus } from '@nova/shared';
import {
  AlertTriangle,
  Ban,
  CalendarClock,
  Clock,
  FileEdit,
  Lightbulb,
  Send,
  Sparkles,
  ThumbsUp,
  XCircle,
} from 'lucide-react';
import { useAreaColor } from '../lib/area-color.js';
import { cn } from '../lib/cn.js';

interface Tone {
  className: string;
  Icon: typeof Lightbulb;
}

/** One consistent colour + icon per status, used everywhere (06). */
const TONES: Record<ContentStatus, Tone> = {
  // Pre-forslag: shown only in the review wizard, never on the board — but a tone
  // is still needed so any incidental badge (e.g. the «Utgått»-strip) renders.
  PROPOSED: { className: 'bg-violet/10 text-violet border-violet/20', Icon: Sparkles },
  SUGGESTED: { className: 'bg-violet/12 text-violet border-violet/25', Icon: Lightbulb },
  APPROVED: { className: 'bg-accent/12 text-accent border-accent/25', Icon: ThumbsUp },
  DRAFT: { className: 'bg-ink-soft/12 text-ink-soft border-ink-soft/25', Icon: FileEdit },
  SCHEDULED: { className: 'bg-warning/12 text-warning border-warning/25', Icon: CalendarClock },
  PUBLISHED: { className: 'bg-success/18 text-success border-success/35', Icon: Send },
  REJECTED: { className: 'bg-danger/12 text-danger border-danger/25', Icon: XCircle },
  CANCELLED: { className: 'bg-ink-soft/8 text-ink-soft border-ink-soft/20', Icon: Ban },
  EXPIRED: { className: 'bg-ink-soft/8 text-ink-soft border-ink-soft/20', Icon: Clock },
};

const OVERDUE_TONE: Tone = {
  className: 'bg-danger/15 text-danger border-danger/35',
  Icon: AlertTriangle,
};

export function StatusBadge({
  status,
  overdue = false,
  size = 'md',
}: {
  status: ContentStatus;
  /** FORSINKET is derived, never stored — it replaces the status chip visually. */
  overdue?: boolean;
  size?: 'sm' | 'md';
}) {
  const tone = overdue ? OVERDUE_TONE : TONES[status];
  const label = overdue ? OVERDUE_LABEL : CONTENT_STATUS_LABELS[status];
  const { Icon } = tone;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border font-medium',
        tone.className,
        size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs',
      )}
    >
      <Icon size={size === 'sm' ? 11 : 13} strokeWidth={2.2} aria-hidden />
      {label}
    </span>
  );
}

/** Small neutral chip for pillar, format, channel and similar metadata. */
export function MetaChip({
  children,
  color,
  className,
}: {
  children: React.ReactNode;
  color?: string;
  className?: string;
}) {
  const areaColor = useAreaColor();

  return (
    <span
      className={cn(
        'border-hairline bg-raised/60 text-ink-soft inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px]',
        className,
      )}
    >
      {color && (
        <span
          aria-hidden
          className="h-1.5 w-1.5 rounded-full"
          style={{ backgroundColor: areaColor(color) }}
        />
      )}
      {children}
    </span>
  );
}

/** Marks anything produced by Nova — violet is the agent colour (06). */
export function AgentMark({ label = 'Nova' }: { label?: string }) {
  return (
    <span className="border-violet/25 bg-violet/12 text-violet inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium">
      <span className="bg-violet h-1.5 w-1.5 rounded-full" aria-hidden />
      {label}
    </span>
  );
}

/**
 * Marks a row the seed script fabricated for the demo. Amber background so it
 * is visible at a glance (07 — 2.1.9), but still small — it is there to be
 * checked when you wonder, not to shout on every card (2.1.8).
 */
export function DemoMark() {
  return (
    <span
      title="Demo-data fra seed-scriptet — ikke noe som faktisk har skjedd"
      className="border-warning/25 bg-warning/12 text-warning inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-medium uppercase"
    >
      demo
    </span>
  );
}
